/**
 * OutletRegister — public page, no auth required.
 * Accessible at /outlet-register
 *
 * Outlets fill this in and submit. Creates a pending record in
 * outlet_registrations for PLD staff to review and approve; approving sends
 * the welcome email (outletWelcomeService). Laid out as a welcome, like the
 * New Customer Form (Joe, 29 Oct 2026): how we work, what happens next.
 */

const ORDERS = 'orders@pleasantlanddistillery.com';

import { useState } from 'react';
import pldLogo from '../assets/pld-logo.png';

const GREEN  = '#22391f';
const YELLOW = '#FFDC06';

const OUTLET_TYPES = [
  { value: 'pub',        label: 'Pub' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'hotel',      label: 'Hotel' },
  { value: 'retailer',   label: 'Retailer' },
  { value: 'online',     label: 'Online / E-commerce' },
  { value: 'wholesale',  label: 'Wholesaler' },
  { value: 'distillery', label: 'Distillery' },
  { value: 'other',      label: 'Other' },
];

const DELIVERY_METHODS = [
  { value: '',               label: '— No preference —' },
  { value: 'DPD',            label: 'DPD courier' },
  { value: 'PALLET_HAULAGE', label: 'Pallet haulage' },
  { value: 'CLIENT_COLLECT', label: 'We will collect' },
  { value: 'IN_HOUSE',       label: 'Pleasant Land delivery' },
];

// ── Shared primitives ─────────────────────────────────────────────────────────
function Field({ label, required, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#2d2d2d' }}>
        {label}{required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && <span style={{ fontSize: 11, color: '#6b7280' }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  padding: '9px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 14,
  background: '#fff',
  width: '100%',
  boxSizing: 'border-box',
  color: '#111',
  outline: 'none',
};

function Input(props) {
  return <input {...props} style={{ ...inputStyle, ...props.style }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ ...inputStyle, ...props.style }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ ...inputStyle, minHeight: 80, resize: 'vertical', ...props.style }} />;
}

function SectionHeader({ number, title, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, margin: '32px 0 20px' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: GREEN, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{number}</div>
      <div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: GREEN }}>{title}</h3>
        {subtitle && <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{subtitle}</p>}
      </div>
    </div>
  );
}

// ── Success screen ────────────────────────────────────────────────────────────
function SuccessScreen() {
  return (
    <div style={{ padding: '44px 32px', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', color: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 800, margin: '0 auto 18px' }}>✓</div>
      <h2 style={{ margin: '0 0 10px', fontSize: 24, fontWeight: 800, color: GREEN }}>Thanks — we've got it!</h2>
      <p style={{ margin: '0 auto 22px', fontSize: 15, color: '#374151', maxWidth: 440, lineHeight: 1.6 }}>
        We'll check your details and set up your trade account, usually within 2 working days. Once it's ready you'll get a welcome email with your account details and a link to set up Direct Debit.
      </p>
      <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
        Questions in the meantime? Email <a href={`mailto:${ORDERS}`} style={{ color: GREEN, fontWeight: 700 }}>{ORDERS}</a>
      </p>
    </div>
  );
}

// How we work, and what happens next — shown above the form.
function Welcome() {
  const card = { flex: '1 1 170px', background: '#f6f7f2', borderRadius: 10, padding: '14px 16px' };
  const head = { fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: GREEN, margin: '0 0 6px' };
  const body = { fontSize: 14, color: '#374151', lineHeight: 1.5, margin: 0 };
  const steps = [
    ['Tell us about you', 'Fill in this form — about 5 minutes.'],
    ['We set you up', 'We check your details, usually within 2 working days.'],
    ["You're ready to order", "You'll get a welcome email with your account and a Direct Debit link."],
  ];
  return (
    <div style={{ padding: '26px 32px 6px' }}>
      <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: GREEN }}>How we work</h2>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ ...card, flex: '1.7 1 270px' }}><p style={head}>Ordering</p><p style={body}>Email your orders to<br /><a href={`mailto:${ORDERS}`} style={{ color: GREEN, fontWeight: 700, overflowWrap: 'anywhere' }}>{ORDERS}</a></p></div>
        <div style={card}><p style={head}>Payment terms</p><p style={body}><strong>7 days</strong> from the invoice date</p></div>
        <div style={card}><p style={head}>Paying</p><p style={body}>By <strong>GoCardless Direct Debit</strong> — taken automatically when it's due</p></div>
      </div>
      <h2 style={{ margin: '24px 0 10px', fontSize: 16, fontWeight: 800, color: GREEN }}>What happens next</h2>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {steps.map(([t, d], i) => (
          <li key={t} style={{ flex: '1 1 170px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: YELLOW, color: GREEN, fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
            <span><strong style={{ fontSize: 14, color: '#1f2937' }}>{t}</strong><br /><span style={{ fontSize: 13, color: '#6b7280' }}>{d}</span></span>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── Main form ─────────────────────────────────────────────────────────────────
const BLANK = {
  submitted_by_name: '',
  submitted_by_email: '',
  submitted_by_phone: '',
  name: '',
  outlet_type: '',
  address_line1: '',
  address_line2: '',
  town: '',
  county: '',
  postcode: '',
  country: 'United Kingdom',
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  recall_contact_name: '',
  recall_contact_email: '',
  recall_contact_phone: '',
  default_delivery_method: '',
  notes: '',
};

export default function OutletRegister() {
  const [form, setForm]       = useState(BLANK);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const res = await fetch('/api/public/outlet-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || json.message || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Header */}
      <div style={{ background: GREEN, padding: '0 24px' }}>
        <div style={{ maxWidth: 660, margin: '0 auto', padding: '20px 0', display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src={pldLogo} alt="" style={{ width: 52, height: 52, borderRadius: '50%', background: '#fff', padding: 3, boxSizing: 'border-box', flexShrink: 0 }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 17, lineHeight: 1.2 }}>Pleasant Land Distillery</div>
            <div style={{ color: 'rgba(255,255,255,.75)', fontSize: 13 }}>Trade account registration</div>
          </div>
        </div>
      </div>

      {/* Yellow accent bar */}
      <div style={{ height: 4, background: YELLOW }} />

      {/* Card */}
      <div style={{ maxWidth: 660, margin: '32px auto', padding: '0 16px 48px' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.1)', overflow: 'hidden' }}>

          {/* Card header */}
          <div style={{ padding: '30px 32px 22px', background: 'linear-gradient(180deg, #f6f7f2 0%, #ffffff 100%)', borderBottom: '1px solid #e5e7eb' }}>
            <h1 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 800, color: GREEN, lineHeight: 1.2 }}>Welcome! Let's set up your trade account</h1>
            <p style={{ margin: 0, fontSize: 15, color: '#4b5563', lineHeight: 1.6 }}>
              We're really pleased you'd like to stock our spirits. Tell us a little about your business below and we'll get your account ready.
            </p>
          </div>

          {submitted ? (
            <SuccessScreen />
          ) : (<>
            <Welcome />
            <form onSubmit={handleSubmit} style={{ padding: '8px 32px 32px' }}>

              {/* 1. Your details */}
              <SectionHeader number={1} title="About you" subtitle="The person filling in this form" />
              <div style={{ display: 'grid', gap: 14 }}>
                <Field label="Your name" required>
                  <Input value={form.submitted_by_name} onChange={e => set('submitted_by_name', e.target.value)} required autoFocus placeholder="Jane Smith" />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                  <Field label="Your email" required>
                    <Input type="email" value={form.submitted_by_email} onChange={e => set('submitted_by_email', e.target.value)} required placeholder="jane@example.com" />
                  </Field>
                  <Field label="Your phone">
                    <Input type="tel" value={form.submitted_by_phone} onChange={e => set('submitted_by_phone', e.target.value)} placeholder="07700 900000" />
                  </Field>
                </div>
              </div>

              {/* 2. Outlet info */}
              <SectionHeader number={2} title="Your business" subtitle="The name your customers know you by" />
              <div style={{ display: 'grid', gap: 14 }}>
                <Field label="Outlet / business name" required>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} required placeholder="The White Horse Inn" />
                </Field>
                <Field label="Type of outlet" required>
                  <Select value={form.outlet_type} onChange={e => set('outlet_type', e.target.value)} required>
                    <option value="">— Please select —</option>
                    {OUTLET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select>
                </Field>
              </div>

              {/* 3. Address */}
              <SectionHeader number={3} title="Delivery address" subtitle="Where should we deliver your orders?" />
              <div style={{ display: 'grid', gap: 14 }}>
                <Field label="Address line 1">
                  <Input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="123 High Street" />
                </Field>
                <Field label="Address line 2">
                  <Input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="(optional)" />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                  <Field label="Town / city">
                    <Input value={form.town} onChange={e => set('town', e.target.value)} placeholder="Canterbury" />
                  </Field>
                  <Field label="County">
                    <Input value={form.county} onChange={e => set('county', e.target.value)} placeholder="Kent" />
                  </Field>
                  <Field label="Postcode">
                    <Input value={form.postcode} onChange={e => set('postcode', e.target.value.toUpperCase())} placeholder="CT1 1AA" />
                  </Field>
                </div>
                <Field label="Country">
                  <Input value={form.country} onChange={e => set('country', e.target.value)} />
                </Field>
              </div>

              {/* 4. Primary contact */}
              <SectionHeader number={4} title="Day-to-day contact" subtitle="Who we should talk to about orders and invoices. Leave blank if it's you." />
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                  <Field label="Name">
                    <Input value={form.primary_contact_name} onChange={e => set('primary_contact_name', e.target.value)} placeholder="Same as above" />
                  </Field>
                  <Field label="Email">
                    <Input type="email" value={form.primary_contact_email} onChange={e => set('primary_contact_email', e.target.value)} placeholder="orders@example.com" />
                  </Field>
                  <Field label="Phone">
                    <Input type="tel" value={form.primary_contact_phone} onChange={e => set('primary_contact_phone', e.target.value)} />
                  </Field>
                </div>
              </div>

              {/* 5. Recall contact */}
              <SectionHeader
                number={5}
                title="Product recall contact"
                subtitle="If we ever had to recall a product, who should we call? Someone we can reach quickly who can take it off sale — a legal must for us."
              />
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                  <Field label="Name">
                    <Input value={form.recall_contact_name} onChange={e => set('recall_contact_name', e.target.value)} />
                  </Field>
                  <Field label="Email">
                    <Input type="email" value={form.recall_contact_email} onChange={e => set('recall_contact_email', e.target.value)} />
                  </Field>
                  <Field label="Phone" required hint="Must be a direct line — not a general switchboard">
                    <Input type="tel" value={form.recall_contact_phone} onChange={e => set('recall_contact_phone', e.target.value)} />
                  </Field>
                </div>
              </div>

              {/* 6. Delivery & notes */}
              <SectionHeader number={6} title="Deliveries & anything else" />
              <div style={{ display: 'grid', gap: 14 }}>
                <Field label="Preferred delivery method">
                  <Select value={form.default_delivery_method} onChange={e => set('default_delivery_method', e.target.value)}>
                    {DELIVERY_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </Select>
                </Field>
                <Field label="Anything else we should know?" hint="Special delivery instructions, access requirements, preferred contact times, etc.">
                  <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="e.g. deliveries must arrive before 11am; ring the side bell" />
                </Field>
              </div>

              {/* Terms note */}
              <div style={{ margin: '24px 0', padding: '14px 16px', background: '#f0fdf4', borderRadius: 8, borderLeft: `3px solid ${GREEN}`, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                By sending this you confirm the details are right and that you can register on behalf of this business. You're also agreeing to our standard terms: payment within <strong>7 days</strong> of invoice, by <strong>GoCardless Direct Debit</strong>. We keep your details safe and only use them to run your account, deliver your orders and contact you about a recall.
              </div>

              {error && (
                <div style={{ margin: '0 0 16px', padding: '12px 16px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                style={{
                  width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                  background: saving ? '#6b7280' : GREEN, color: '#fff', fontSize: 15, fontWeight: 700,
                  transition: 'background .15s',
                }}
              >
                {saving ? 'Sending…' : 'Send my registration'}
              </button>
            </form>
          </>)}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#9ca3af' }}>
          © {new Date().getFullYear()} Pleasant Land Distillery Ltd · Est. 2022
        </p>
      </div>
    </div>
  );
}
